import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowLeft, Sparkles, Heart, Trash2, Edit3, Check, X, Crown, Lock } from "lucide-react";
import { toast } from "sonner";
import { API } from "../App";
import Footer from "../components/Footer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

export default function Affirmations({ auth }) {
  const navigate = useNavigate();
  const [affirmations, setAffirmations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    checkPremiumAndFetch();
  }, []);

  const checkPremiumAndFetch = async () => {
    try {
      // Check subscription status
      const statusRes = await fetch(`${API}/subscription/status`, { credentials: 'include' });
      if (statusRes.ok) {
        const status = await statusRes.json();
        const premium = status.plan !== 'free';
        setIsPremium(premium);

        if (premium) {
          // Fetch saved affirmations
          const affRes = await fetch(`${API}/affirmations`, { credentials: 'include' });
          if (affRes.ok) {
            const data = await affRes.json();
            setAffirmations(data);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching affirmations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = (aff) => {
    setEditingId(aff.affirmation_id);
    setEditName(aff.name || "");
  };

  const handleSaveEdit = async (affirmationId) => {
    try {
      const response = await fetch(`${API}/affirmations/${affirmationId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: editName })
      });

      if (response.ok) {
        setAffirmations(prev => prev.map(a => 
          a.affirmation_id === affirmationId ? { ...a, name: editName } : a
        ));
        toast.success("Affirmation renamed");
      } else {
        toast.error("Couldn't save changes");
      }
    } catch (error) {
      toast.error("Something went wrong");
    }
    setEditingId(null);
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const response = await fetch(`${API}/affirmations/${deleteId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setAffirmations(prev => prev.filter(a => a.affirmation_id !== deleteId));
        toast.success("Affirmation removed");
      } else {
        toast.error("Couldn't delete affirmation");
      }
    } catch (error) {
      toast.error("Something went wrong");
    }
    setDeleteId(null);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="w-12 h-12 border-2 border-[#A87C6D] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#6E6A66] font-light">Loading your affirmations...</p>
        </div>
      </div>
    );
  }

  // Not premium - show upgrade prompt
  if (!isPremium) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]" data-testid="affirmations-locked">
        <nav className="px-6 py-4 border-b border-[#E6E2DD]">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <Link to="/profile">
              <Button variant="ghost" className="rounded-full text-[#6E6A66] hover:text-[#2D2A26] hover:bg-[#E6E2DD]">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
            </Link>
            <span 
              className="text-xl font-light text-[#2D2A26]"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              My Affirmations
            </span>
            <div className="w-20"></div>
          </div>
        </nav>

        <main className="max-w-xl mx-auto px-6 py-16">
          <div className="text-center animate-fade-in">
            <div className="w-20 h-20 rounded-full bg-[#A87C6D]/10 flex items-center justify-center mx-auto mb-8">
              <Lock className="w-10 h-10 text-[#A87C6D]" />
            </div>
            <h1 
              className="text-3xl font-light text-[#2D2A26] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Unlock Custom Affirmations
            </h1>
            <p className="text-[#6E6A66] font-light mb-8 max-w-md mx-auto leading-relaxed">
              Custom affirmations are personalized reminders of what's already true—generated from your own reflections and saved for when you need them.
            </p>
            
            <div className="paper-card rounded-2xl p-6 mb-8 text-left">
              <h3 className="text-sm font-medium text-[#2D2A26] mb-4">With Inner Circle, you can:</h3>
              <ul className="space-y-3 text-[#6E6A66] font-light text-sm">
                <li className="flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-[#A87C6D] mt-0.5 flex-shrink-0" />
                  Receive personalized affirmations after check-ins
                </li>
                <li className="flex items-start gap-2">
                  <Heart className="w-4 h-4 text-[#A87C6D] mt-0.5 flex-shrink-0" />
                  Save affirmations to revisit later
                </li>
                <li className="flex items-start gap-2">
                  <Edit3 className="w-4 h-4 text-[#A87C6D] mt-0.5 flex-shrink-0" />
                  Rename and organize your collection
                </li>
              </ul>
            </div>

            <Button
              onClick={() => navigate('/subscription')}
              className="rounded-full px-8 py-5 bg-[#A87C6D] hover:bg-[#956d5f] text-white"
            >
              <Crown className="w-4 h-4 mr-2" />
              Upgrade to Inner Circle
            </Button>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  // Premium user - show affirmations
  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="affirmations-page">
      <nav className="px-6 py-4 border-b border-[#E6E2DD]">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Link to="/profile">
            <Button variant="ghost" className="rounded-full text-[#6E6A66] hover:text-[#2D2A26] hover:bg-[#E6E2DD]">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
          </Link>
          <span 
            className="text-xl font-light text-[#2D2A26]"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            My Affirmations
          </span>
          <div className="w-20"></div>
        </div>
      </nav>

      <main className="max-w-xl mx-auto px-6 py-12">
        <div className="animate-fade-in">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="w-16 h-16 rounded-full bg-[#A87C6D]/10 flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-8 h-8 text-[#A87C6D]" />
            </div>
            <h1 
              className="text-2xl font-light text-[#2D2A26] mb-2"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Your Saved Affirmations
            </h1>
            <p className="text-[#6E6A66] font-light text-sm">
              Quiet truths you can return to.
            </p>
          </div>

          {/* Affirmations List */}
          {affirmations.length === 0 ? (
            <div className="text-center py-12">
              <Heart className="w-12 h-12 text-[#E6E2DD] mx-auto mb-4" />
              <p className="text-[#6E6A66] font-light mb-2">No saved affirmations yet</p>
              <p className="text-[#A8A4A0] text-sm">
                Complete a check-in to receive your first personalized affirmation.
              </p>
              <Button
                onClick={() => navigate('/check-in')}
                className="mt-6 rounded-full px-6 bg-[#A87C6D] hover:bg-[#956d5f] text-white"
              >
                Start a check-in
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {affirmations.map((aff) => (
                <div 
                  key={aff.affirmation_id}
                  className="paper-card rounded-2xl p-5 group"
                >
                  {editingId === aff.affirmation_id ? (
                    <div className="flex items-center gap-2 mb-3">
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="Give this affirmation a name..."
                        className="flex-1 text-sm border-[#E6E2DD] focus:border-[#A87C6D] focus:ring-[#A87C6D]"
                        autoFocus
                      />
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleSaveEdit(aff.affirmation_id)}
                        className="text-[#7A9E7E] hover:bg-[#7A9E7E]/10"
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setEditingId(null)}
                        className="text-[#A8A4A0] hover:bg-[#E6E2DD]"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    aff.name && (
                      <p className="text-xs uppercase tracking-wider text-[#A87C6D] mb-2">
                        {aff.name}
                      </p>
                    )
                  )}
                  
                  <p 
                    className="text-[#2D2A26] font-light leading-relaxed italic"
                    style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.1rem' }}
                  >
                    "{aff.text}"
                  </p>
                  
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#E6E2DD]">
                    <span className="text-xs text-[#A8A4A0]">
                      {formatDate(aff.created_at)}
                    </span>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleStartEdit(aff)}
                        className="w-8 h-8 text-[#A8A4A0] hover:text-[#6E6A66] hover:bg-[#E6E2DD]"
                      >
                        <Edit3 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setDeleteId(aff.affirmation_id)}
                        className="w-8 h-8 text-[#A8A4A0] hover:text-[#C8553D] hover:bg-[#C8553D]/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-[#FDFBF7] border-[#E6E2DD]">
          <AlertDialogHeader>
            <AlertDialogTitle 
              className="text-xl font-normal text-[#2D2A26]"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Remove this affirmation?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6E6A66]">
              This affirmation will be permanently deleted from your collection.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full border-[#E6E2DD]">
              Keep it
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="rounded-full bg-[#C8553D] hover:bg-[#B8452D] text-white"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Footer />
    </div>
  );
}
